<template>
  <div class="doc-preview-container">
    <div v-if="!pdfViewerError" class="pdf-viewer-wrapper">
      <DocumentPdfViewer
        ref="documentPdfViewer"
        class="DocumentPdfViewer"
        :class="{ loading: !pdfViewerInitialized }"
        :mnemo="mnemo"
        :file-id="fileId"
        :menu-offset="menuOffset"
        :menu-transition="false"
        @initialized="onPdfViewerInitialized"
        @pages-loaded="onPagesLoaded"
        @load-file-from-pdfjs-error="onPdfViewerError"
      />
      
      <div v-if="!pdfViewerInitialized" class="loading-overlay">
        <v-progress-circular
          indeterminate
          size="48"
          color="primary"
        />
        <p class="mt-4 text-body-1">{{ $t('common.DocumentViewer.loadingDocumentLabel') }}</p>
      </div>
    </div>
    <div v-else class="pdf-error-placeholder">
      <div class="d-flex flex-column align-center justify-center h-100 text-grey-500">
        <app-material-icon name="file-pdf" size="xl" type="fa" class="mb-4" />
        <p class="text-body-1">{{ $t('project.checklist.dialogs.SmartImportPreview.pdfUnavailable') }}</p>
      </div>
    </div>

    <div class="right-panel bg-white" :class="{ 'no-toolbar': pdfViewerError || !pdfViewerInitialized }">
      <div class="document-header px-6 py-3 d-flex align-center justify-space-between">
        <span class="text--large font-weight-medium">{{ fileName }}</span>
        <div class="d-flex column-gap-1 align-center">
          <span>{{ $t('project.checklist.dialogs.SmartImportPreview.feedbackQuestion') }}</span>
          <app-button
            data-locator="positive-feedback-button"
            :class="{ 'thumb-up-button': true, active: isSmartImportUpvoted }"
            type="icon"
            @click="handleUpvote"
          >
            <app-material-icon name="thumb_up" color="#4F565D" :filled="isSmartImportUpvoted" />
          </app-button>
          <app-button
            data-locator="negative-feedback-button"
            :class="{ 'thumb-down-button': true, active: isSmartImportDownvoted }"
            type="icon"
            @click="handleDownvote"
          >
            <app-material-icon name="thumb_down" color="#4F565D" :filled="isSmartImportDownvoted" />
          </app-button>
        </div>
      </div>

      <div class="detection-block">
        <div class="detection-header">
          <div class="d-flex align-center">
            <AIAssistantIcon :width="24" :height="24" class="mr-2" />
            <span class="text--medium text--bold">{{ $t('project.checklist.dialogs.SmartImportPreview.detectionTitle') }}</span>
          </div>
        </div>
        
        <div class="detection-content">
          <template v-if="importChecklistFromWordPending">
            <v-skeleton-loader type="image" />
          </template>
          <template v-else>
            <p class="text--small text-grey-700">
              {{ $t('project.checklist.dialogs.SmartImportPreview.disclaimer') }}
            </p>

            <div class="structure-list">
              <SmartImportStructureItem
                v-for="item in preview"
                :key="item.id"
                :item="item"
                :level="0"
              />
            </div>
          </template>
        </div>
      </div>
    </div>
  </div>
</template>

<script lang="ts">
import DocumentPdfViewer from '@/common/document-viewer/DocumentPdfViewer.vue';
import SmartImportStructureItem from './SmartImportStructureItem.vue';
import AIAssistantIcon from '@/common/ai-assistant/AIAssistantIcon.vue';
import { defineComponent, PropType } from 'vue'
import { mapGetters, mapState } from 'vuex';

export default defineComponent({
  name: 'SmartImportPreview',
  components: {
    DocumentPdfViewer,
    SmartImportStructureItem,
    AIAssistantIcon,
  },
  props: {
    fileId: {
      type: Number as PropType<number>,
      required: true,
    },
    fileName: {
      type: String,
      required: true,
    },
    preview: {
      type: Object as PropType<any>,
      required: false,
      default: null,
    },
  },
  data() {
    return {
      isSmartImportUpvoted: false,
      isSmartImportDownvoted: false,
      pdfViewerError: false,
      pdfViewerInitialized: false,
      windowWidth: window.innerWidth,
    }
  },
  computed: {
    ...mapGetters('room', { mnemo: 'roomMnemo' }),
    ...mapState('checklist', ['importChecklistFromWordPending']),
    menuOffset() {
      const offsetVertical = -400
      const offsetHorizontal = this.windowWidth >= 1440 ? -300 : 0
      return [offsetVertical, offsetHorizontal]
    },
  },
  mounted() {
    window.addEventListener('resize', this.handleResize)
  },
  methods: {
    handleUpvote(): void {
      if (this.isSmartImportUpvoted) {
        this.isSmartImportUpvoted = false
      } else {
        this.isSmartImportUpvoted = true
        this.isSmartImportDownvoted = false
      }
    },
    handleDownvote(): void {
      if (this.isSmartImportDownvoted) {
        this.isSmartImportDownvoted = false
      } else {
        this.isSmartImportDownvoted = true
        this.isSmartImportUpvoted = false
      }
    },
    handleResize(): void {
      this.windowWidth = window.innerWidth
    },
    onPdfViewerInitialized(): void {},
    onPagesLoaded(): void {
      this.pdfViewerInitialized = true
      const documentPdfViewer = this.$refs['documentPdfViewer'] as any
      const pdfAppInstance = documentPdfViewer?.getPdfAppInstance?.()
      if (pdfAppInstance?.appConfig?.sidebar?.outerContainer) {
        pdfAppInstance.appConfig.sidebar.outerContainer.classList.add('rightSidebarOpen')
      }
      setTimeout(() => {
        documentPdfViewer?.setZoomLevel?.('auto')
        documentPdfViewer?.onViewportChanged?.()
      }, 200)
    },
    onPdfViewerError(): void {
      this.pdfViewerError = true
    },
  },
  errorCaptured(err: unknown): boolean {
    this.pdfViewerError = true
    return false
  },
  beforeUnmount(): void {
    window.removeEventListener('resize', this.handleResize)
    const pdfViewer = (this.$refs['documentPdfViewer'] as any)?.getPdfAppInstance?.()
    if (pdfViewer?.appConfig?.sidebar?.outerContainer) {
      pdfViewer.appConfig.sidebar.outerContainer.classList.remove('rightSidebarOpen')
    }
  },
})
</script>

<style lang="scss" scoped>
.doc-preview-container {
  position: relative;
  height: 75vh;
  max-height: 650px;
  min-height: 400px;
  width: 100%;
  border-bottom: 1px solid #e0e0e0;
  border-top: 1px solid #e0e0e0;

  .pdf-viewer-wrapper {
    width: 100%;
    height: 100%;
    position: relative;
    overflow: hidden;
  }

  .loading-overlay {
    position: absolute;
    top: 0;
    left: 0;
    right: 480px;
    bottom: 0;
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    background-color: #f5f5f5;
    z-index: 100000;
  }

  .pdf-error-placeholder {
    width: calc(100% - 480px);
    height: 100%;
    background-color: #f5f5f5;
  }

  .right-panel {
    position: absolute;
    top: 58px;
    right: 0;
    width: 480px;
    height: calc(100% - 58px);
    overflow-y: auto;
    z-index: 1;
    border-left: 1px solid #e0e0e0;
    display: flex;
    flex-direction: column;

    &.no-toolbar {
      top: 0;
      height: 100%;
    }

    .document-header {
      border-bottom: 1px solid #e0e0e0;
    }

    .detection-block {
      overflow: hidden;
      display: flex;
      flex-direction: column;
      flex: 1;
      padding: 24px 0 0 24px;

      .detection-content {
        display: flex;
        flex-direction: column;
        flex: 1;
        gap: 12px;
        overflow: hidden;
      }

      .structure-list {
        flex: 1;
        overflow-y: auto;
      }
    }
  }

  :deep(.DocumentPdfViewer) {
    width: 100%;
    height: 100%;
    
    &.loading {
      visibility: hidden;
    }
  }
  
  :deep(#outerContainer.rightSidebarOpen #viewerContainer:not(.pdfPresentationMode)) {
    inset-inline-end: 480px;
    transition-property: inset-inline-start, inset-inline-end;
  }
}
</style>
